import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Select } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";

import { Loading } from "@/shared/components/Loading/Loading";
import { TestItem } from "@/shared/components/assessment/new/TestItem/TestItem";

import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { TestHttpService } from "@/shared/services/tests/test-http.service";
import { getTestDisplayName } from "@/shared/services/tests/tests.utils";
import { INormSummary } from "@/shared/services/norms/norms-http.service";

import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";

import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";

import { ReactComponent as CheckedSVG } from "@/assets/icons/checked.svg";

import { useAllClientTestsHook } from "../../hooks/useAllClientTestsHook";
import { NormHttpService } from "./services/norm-http.service";
import { IAssessmentTest, IClientTest } from "../../types/test.interface";
import { IAssessment } from "../../assessments.types";

import "./index.css";
import "../../styles/assessmentCommon.css";
import style from "./style.module.css";
import { groupNormsByTestId } from "./hooks/useBatchTestNorms.hook";

export function AddTests({ isChangeable = true, isArchived = false }: { isChangeable?: boolean; isArchived: boolean }) {
  const { client_id: clientId, assessment_id: assessmentId } = useParams<{
    client_id: string;
    assessment_id: string;
  }>();

  const { assessment, setAssessment } = useContext(AssessmentContext);
  const { tests, isLoading } = useAllClientTestsHook(Number(clientId), {
    standaloneOnly: true,
    assessmentId: assessmentId ? Number(assessmentId) : undefined,
  });

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectedTests, setSelectedTests] = useState<IAssessmentTest[]>([]);
  const [normsMap, setNormsMap] = useState<{ [testId: number]: INormSummary[] }>({});
  const [isLoadingNorms, setIsLoadingNorms] = useState<boolean>(false);
  const hasInitialLoad = useRef<boolean>(false);

  const initialPlaceholderText = isArchived
    ? "You cannot add tests to the archived assessment"
    : isChangeable
    ? "Select tests"
    : "You cannot add tests, someone accepted this assessment";
  const [placeholder, setPlaceholder] = useState<string>(initialPlaceholderText);

  useEffect(() => {
    if (assessment?.assessmentTests) {
      setSelectedTests([...assessment.assessmentTests]);
      setSelectedItems(assessment.assessmentTests.map(({ test }) => test.testId));
    }
  }, [assessment]);

  useEffect(() => {
    if (!hasInitialLoad.current && assessment) {
      hasInitialLoad.current = true;

      const assessmentTests = assessment.assessmentTests;
      if (!assessmentTests || assessmentTests.length === 0) {
        setIsLoadingNorms(false);
        return;
      }
      setIsLoadingNorms(true);
      const areNormsSelectable = !isArchived && isChangeable;
      const testIds = assessmentTests.map((at) => at.test.testId);

      const fetchPromise = areNormsSelectable
        ? NormHttpService.getNormsByTestIds(Number(clientId), assessment.assessmentId, testIds)
        : NormHttpService.getNormsByAssessment(Number(clientId), assessment.assessmentId);
      fetchPromise
        .then((norms: INormSummary[]) => {
          const grouped = groupNormsByTestId(norms);
          setNormsMap(grouped);
        })
        .catch((e) => {
          console.error(`Error fetching norms: ${e}`);
        })
        .finally(() => {
          setIsLoadingNorms(false);
        });
    }
  }, [assessment, clientId, isArchived, isChangeable]);

  const onTestChange = async (event: number[]) => {
    let isIncreased = event.length > selectedItems.length;

    const difference = isIncreased
      ? event.filter((item) => !selectedItems.includes(item))
      : selectedItems.filter((item) => !event.includes(item));

    const newTestIds: number[] = [];

    for (let i = 0; i < difference.length; i++) {
      const updatedTest: IClientTest | undefined = tests.find((item) => item.testId === difference[i]);

      if (!updatedTest) {
        return;
      }

      try {
        if (isIncreased) {
          await TestHttpService.addTestToAssessment(
            String(clientId),
            String(assessmentId),
            String(updatedTest?.testId),
            {
              rank: selectedItems.length + 1,
            },
          );
          newTestIds.push(updatedTest.testId);
        } else {
          await TestHttpService.deleteTestFromAssessmentById(
            String(clientId),
            String(assessmentId),
            String(updatedTest?.testId),
          );
          setNormsMap((prev) => {
            const updated = { ...prev };
            delete updated[updatedTest.testId];
            return updated;
          });
        }
      } catch (e) {
        console.error(`Update tests error: ${e}`);
      }
    }

    setSelectedItems(event);

    const updAssessment: IAssessment = await AssessmentHttpService.getAssessmentByID(Number(assessmentId));
    if (updAssessment.assessmentTests) {
      setSelectedTests([...updAssessment.assessmentTests]);

      // Fetch norms only for newly added test(s)
      if (isIncreased && newTestIds.length > 0) {
        try {
          const newNorms = await NormHttpService.getNormsByTestIds(
            Number(clientId),
            updAssessment.assessmentId,
            newTestIds,
          );
          const grouped = groupNormsByTestId(newNorms);
          setNormsMap((prev) => ({ ...prev, ...grouped }));
        } catch (e) {
          console.error(`Error fetching norms for new test: ${e}`);
        }
      }
    }

    if (setAssessment) {
      setAssessment(updAssessment);
    }
  };

  const handleDeleteTest = async (id: number) => {
    if (!assessment || !assessment.assessmentTests) {
      return;
    }

    const testForRemove: IClientTest | undefined = tests.find((item) => item.testId === id);

    await TestHttpService.deleteTestFromAssessmentById(
      String(clientId),
      String(assessmentId),
      String(testForRemove?.testId),
    );

    setNormsMap((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });

    const updAssessment: IAssessment = await AssessmentHttpService.getAssessmentByID(Number(assessmentId));
    if (updAssessment.assessmentTests) {
      setSelectedTests([...updAssessment.assessmentTests]);
      setSelectedItems(updAssessment.assessmentTests.map(({ test }) => test.testId));
    }

    if (setAssessment) {
      setAssessment(updAssessment);
    }
  };

  const onDragEnd = async ({ source, destination }: DropResult) => {
    if (!destination) {
      return;
    }

    const rangedTests = Array.from(selectedTests);
    const [newOrder] = rangedTests.splice(source.index, 1);
    rangedTests.splice(destination.index, 0, newOrder);

    const rank = destination.index + 1;

    // TODO: Move to utils
    const filteredRank = rangedTests
      .map((aT) => {
        if (aT.testId === newOrder.test.testId) {
          return { ...aT, rank };
        }

        if (rank === aT.rank) {
          if (rank > newOrder.rank) {
            return { ...aT, rank: aT.rank - 1 };
          }

          if (rank < newOrder.rank) {
            return { ...aT, rank: aT.rank + 1 };
          }
        }

        return { ...aT, rank: rank > aT.rank ? aT.rank - 1 : aT.rank + 1 };
      })
      .sort((a, b) => {
        if (a.rank > b.rank) {
          return 1;
        }

        if (a.rank < b.rank) {
          return -1;
        }

        return 0;
      })
      .map((aT, index) => ({ ...aT, rank: index + 1 }));
    setSelectedTests(filteredRank);

    await TestHttpService.updateTestInAssessment(String(clientId), String(assessmentId), String(newOrder.test.testId), {
      rank,
    });

    const updAssessment: IAssessment = await AssessmentHttpService.getAssessmentByID(Number(assessmentId));
    if (updAssessment.assessmentTests) {
      setSelectedTests([...updAssessment.assessmentTests]);
    }
    if (setAssessment) {
      setAssessment(updAssessment);
    }
  };

  const onNormChange = async (testId: number, normId: number) => {
    await TestHttpService.updateTestInAssessment(String(clientId), String(assessmentId), String(testId), {
      normId,
    });
    setSelectedTests((prev) => {
      return prev.map((test) => {
        if (test.testId === testId) {
          return { ...test, normId };
        }
        return test;
      });
    });
  };

  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canManageAssessment = assessmentAbility.can(Action.Manage, Subject.ASSESSMENT);
  const canViewAssessment = assessmentAbility.can(Action.Read, Subject.ASSESSMENT);
  const hasAssessmentTemplate = Boolean(assessment?.template);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container">
      <div className="addTestContainer">
        {hasAssessmentTemplate ? (
          <div className={style.templateMessageContainer}>
            <ExclamationCircleOutlined className={style.templateMessageIcon} />
            <p className={style.templateMessageText}>
              <strong>You cannot edit the tests that appear within an Assessment Template</strong>
            </p>
          </div>
        ) : (
          <div className="selectContainer">
            <Select
              mode="multiple"
              value={[...selectedItems]}
              showArrow
              placeholder={placeholder}
              maxTagPlaceholder={placeholder}
              onDropdownVisibleChange={(open) => {
                open ? setPlaceholder("") : setPlaceholder(initialPlaceholderText);
              }}
              maxTagCount={0}
              onChange={onTestChange}
              className="selector"
              menuItemSelectedIcon={<CheckedSVG />}
              disabled={isArchived || !isChangeable || !canManageAssessment}
              dropdownMatchSelectWidth={750}
              placement="bottomLeft"
              dropdownAlign={{ offset: [0, 10] }}
              options={tests.map((item) => {
                return {
                  label: getTestDisplayName(item),
                  value: item.testId,
                };
              })}
              optionFilterProp="label"
            />
          </div>
        )}

        {selectedTests.length === 0 && <p>You haven't added any test yet</p>}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={"testsList"} isDropDisabled={!canManageAssessment || isArchived || !isChangeable}>
            {(provided) => (
              <div className={style.testList} {...provided.droppableProps} ref={provided.innerRef}>
                {selectedTests
                  .sort((a, b) => a.rank - b.rank)
                  .map(({ testId, normId, test }, index) => (
                    <Draggable
                      key={testId}
                      draggableId={String(testId)}
                      index={index}
                      isDragDisabled={isArchived || !isChangeable || !canManageAssessment}
                    >
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <TestItem
                            isChangeable={!isArchived && isChangeable && canManageAssessment}
                            canExpandElements={canViewAssessment}
                            id={testId}
                            normId={normId}
                            name={test.name}
                            internalName={test.internalName}
                            elementList={test.testElements}
                            onDeleteClick={canManageAssessment && handleDeleteTest}
                            norms={normsMap[test.testId] || []}
                            onNormChange={(normId) => onNormChange(testId, normId)}
                            isLoading={isLoadingNorms}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}
